import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Layout from "../components/Layout";
import images from "../common/images";
import { useFetchEvents } from "../hooks/useFetchEvents";
import { formatTimeRange } from "../utils/common";
import FilterComp from "../components/FilterComp";
import Hero from "../components/Hero";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from "../components/Card";
import CityDropDown from "../components/CityDropDown";
import FavouriteIcon from "../components/FavouriteIcon";
import EventCard from "../components/EventCard";
import NearbyEventsList from "../components/NearbyEventsList";
import EventsCarousel from "../components/EventsCarousel";
import useNearbyEvents from "../hooks/useNearbyEvents";
import useUserLocation from "../hooks/useUserLocation";
import EventsCarouselV2 from "../components/EventCarouselV2";
import EventsGallery from "../components/EventsGallery";
import AnimatedSearchBar from "../components/AnimatedSearchBar";
import NoContentAvailable from "../components/NoContentAvailable";
import PageSeoContent from "../components/PageSeoContent";
import LocationRetryPrompt from "../components/LocationRetryPrompt";
import useRequestLocationAccess from "../hooks/useRequestLocationAccess";
import {
  currentCity,
  setManualSelection,
  setSelected,
  userCurrentCity,
} from "../redux/reducers/citySelectorSlice";
import {
  getClubsCitySlug,
  getEventsCityEntry,
  resolveEventsCitySeo,
  resolveRouteSeo,
  getCanonicalUrl,
} from "../config/seo";
import {
  getCityLabelFromSlug,
  getEventsListPath,
  resolveSupportedCityLabel,
  resolveSupportedCitySlug,
  DEFAULT_CITY_SLUG,
} from "../utils/clubPath";

const Events = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cityOrId } = useParams();
  const applyingCityFromUrl = useRef(false);
  const [filteredData, setFilteredData] = useState({});
  const [filterComponent, setFilterComponent] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewAll, setViewAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedCity = useSelector((state) => state.citySelector.selectedCity);
  const userBaseCity = useSelector((state) => state.citySelector.userBaseCity);
  const isManualCitySelection = useSelector(
    (state) => state.citySelector.isManualSelection
  );

  const citySlug = resolveSupportedCitySlug(cityOrId || selectedCity) || DEFAULT_CITY_SLUG;
  const cityLabel = getCityLabelFromSlug(cityOrId || selectedCity);
  const citySeoEntry = useMemo(
    () => getEventsCityEntry(cityLabel),
    [cityLabel],
  );

  const pageSeo = useMemo(() => {
    const citySeo = resolveEventsCitySeo(cityLabel);
    if (citySeo) {
      const { h1, ...seo } = citySeo;
      return seo;
    }
    const base = resolveRouteSeo("/events");
    return {
      ...base,
      canonical: getCanonicalUrl(`/events/${citySlug}`),
    };
  }, [cityLabel, citySlug]);

  useEffect(() => {
    if (!cityOrId) return;

    const supportedSlug = resolveSupportedCitySlug(cityOrId);
    if (!supportedSlug) {
      navigate(getEventsListPath(DEFAULT_CITY_SLUG), { replace: true });
      return;
    }

    if (cityOrId !== supportedSlug) {
      navigate(getEventsListPath(supportedSlug), { replace: true });
    }
  }, [cityOrId, navigate]);

  // Apply /events/goa into Redux so listings match the URL.
  useEffect(() => {
    if (!cityOrId) return;

    const label = resolveSupportedCityLabel(cityOrId);
    if (!label) return;

    const alreadySelected =
      getClubsCitySlug(selectedCity) === getClubsCitySlug(label) ||
      `${selectedCity || ""}`.trim().toLowerCase() === label.toLowerCase();

    if (alreadySelected) return;

    applyingCityFromUrl.current = true;
    dispatch(currentCity(label));
    dispatch(userCurrentCity(label));
    dispatch(setSelected(true));
    dispatch(setManualSelection(true));
  }, [cityOrId, dispatch, selectedCity]);

  // City change → /events/goa
  useEffect(() => {
    if (applyingCityFromUrl.current) {
      applyingCityFromUrl.current = false;
      return;
    }

    if (!selectedCity) return;

    const nextSlug = resolveSupportedCitySlug(selectedCity);
    const currentSlug = resolveSupportedCitySlug(cityOrId);

    if (nextSlug && nextSlug !== currentSlug) {
      navigate(getEventsListPath(nextSlug), { replace: true });
    }
  }, [selectedCity, cityOrId, navigate]);

  const {
    locationData,
    coords,
    locationStatus,
    permissionState,
    error: locationError,
    refreshLocation,
  } = useUserLocation();
  const requestLocationAccess = useRequestLocationAccess(refreshLocation);

  const userCoordinates = useMemo(() => {
    if (!Number.isFinite(coords?.latitude) || !Number.isFinite(coords?.longitude)) {
      return null;
    }

    return {
      latitude: Number(coords?.latitude),
      longitude: Number(coords?.longitude),
    };
  }, [coords?.latitude, coords?.longitude]);

  const hasLiveCoordinates = !!userCoordinates;

  const formatDateForAPI = (date) => {
    return date ? moment(date).format("YYYY-MM-DD") : null;
  };

  const formattedDate = formatDateForAPI(selectedDate);
  const filterEventsBySelectedDate = (eventList) => {
    if (!formattedDate) return eventList;

    return eventList.filter((event) =>
      moment(event?.eventStartTime).isSame(formattedDate, "day")
    );
  };

  const eventCoordinates = isManualCitySelection ? null : coords;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useFetchEvents(filteredData, formattedDate, selectedCity, userBaseCity, true, eventCoordinates);

  const {
    data: fallbackData,
    isLoading: isLoadingFallback,
    isFetching: isFetchingFallback,
  } = useFetchEvents(
    filteredData,
    null,
    selectedCity,
    userBaseCity,
    !!formattedDate,
    eventCoordinates
  );

  const handleViewAll = () => {
    setViewAll(!viewAll);
  };

  const events =
    data?.pages?.flatMap((page) => page?.data || []).filter(Boolean) || [];
  const fallbackEvents =
    fallbackData?.pages?.flatMap((page) => page?.data || []).filter(Boolean) || [];
  const selectedDateEvents = filterEventsBySelectedDate(events);
  const selectedDateFallbackEvents = filterEventsBySelectedDate(fallbackEvents);
  const visibleEvents = selectedDateEvents.length ? selectedDateEvents : selectedDateFallbackEvents;
  const showPageLoader = (isLoading || isFetching || isLoadingFallback || isFetchingFallback) && !visibleEvents.length;

  const {
    data: nearbyEventsPage,
    isLoading: isLoadingNearby,
    isFetching: isFetchingNearby,
  } = useNearbyEvents({
    coordinates: coords,
    date: formattedDate,
    limit: 4,
    ...filteredData,
  });

  const {
    data: fallbackNearbyEventsPage,
    isLoading: isLoadingNearbyFallback,
    isFetching: isFetchingNearbyFallback,
  } = useNearbyEvents({
    coordinates: coords,
    date: null,
    limit: 4,
    enabled: !!formattedDate,
    ...filteredData,
  });

  let eventsNearMe =
    nearbyEventsPage?.pages?.flatMap((page) => page?.data || []).filter(Boolean) || [];
  const fallbackEventsNearMe =
    fallbackNearbyEventsPage?.pages?.flatMap((page) => page?.data || []).filter(Boolean) || [];
  eventsNearMe = filterEventsBySelectedDate(eventsNearMe);
  const selectedDateFallbackEventsNearMe = filterEventsBySelectedDate(fallbackEventsNearMe);
  if (!eventsNearMe.length) {
    eventsNearMe = selectedDateFallbackEventsNearMe;
  }
  const hasEventsNearMe = Array.isArray(eventsNearMe) && eventsNearMe.length > 0;
  if (hasEventsNearMe && eventsNearMe.length <= 3) {
    eventsNearMe = [...eventsNearMe, ...eventsNearMe, ...eventsNearMe];
  }


  const onPressApply = async (data) => {
    setFilteredData(data);
    setFilterComponent(false);
  };

  const onPressCancel = () => {
    setFilterComponent(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log("Form submitted with search term:", searchTerm);
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
  };

  return (
    <Layout seo={pageSeo}>
      <Hero title="EVENTS CALENDAR" />
      <div
        className="hero fillter-sec section-gap wow fadeInUp"
        data-wow-delay="0.2s"
      >
        <div className="container">
          <form className="location-sec" onSubmit={handleSubmit}>
            <div className="row align-items-center">
              <div className="col-lg-3 col-md-6 mb-md-4 mb-xl-0">
                <CityDropDown
                  onPress={(city) => {
                    navigate(getEventsListPath(city));
                  }}
                />
              </div>
              <div className="col-lg-4 col-md-6 mb-md-4 mb-xl-0">
                <AnimatedSearchBar
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSubmit(event);
                    }
                  }}
                />
              </div>
              <div className="col-lg-2 col-md-6 mb-md-4 mb-xl-0">
                <div className="fillter-button wow fadeInUp" data-wow-delay="0.5s">
                  <button
                    className="btn w-100"
                    type="button"
                    data-toggle="modal"
                    data-target="#exampleModal"
                    onClick={() => setFilterComponent(true)}
                  >
                    <i className="fa fa-cog"></i> Filter
                  </button>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="date-button wow fadeInUp" data-wow-delay="0.5s">
                  <div className="date-picker-wrapper">
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                      className="form-control w-100"
                      placeholderText="Select Date"
                      minDate={new Date()}
                    />
                    <span className="calendar-icon">
                      <i className="far fa-calendar-alt"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <PageSeoContent variant="events" title={citySeoEntry?.h1} />

      {/* Events Near Me */}
      {/* {JSON.stringify({ eventsNearMe })} */}
      {!hasLiveCoordinates ? (
        <section className="event-sec D-view section-gap-md">
          <div className="container">
            <div className="mb-4">
              <LocationRetryPrompt
                permissionDenied={
                  permissionState === "denied" || locationStatus === "denied"
                }
                locationError={locationError}
                onRetryLocation={requestLocationAccess}
                description="Apne najdiki events dekhne ke liye browser se location allow karein. Neeche selected city ke events dikh rahe hain."
              />
            </div>
          </div>
        </section>
      ) : isLoadingNearby ? (
        <section className="event-sec D-view section-gap-md">
          <div className="container">
            <div className="d-flex justify-content-center align-items-center py-5">
              <LoadingSpinner className="spinner-container--page" />
            </div>
          </div>
        </section>
      ) : hasEventsNearMe ? (
        <section className="event-sec D-view section-gap-md">
          <div className="container">
            <div className="row">
              <div className="col-lg-12 mb-4">
                <div className="title-sec wow fadeInUp" data-wow-delay="0.3s">
                  <h2 className="title-25">Events Near Me</h2>
                </div>
              </div>

              <div className="col-lg-12">
                <EventsGallery data={eventsNearMe} />
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="event-sec D-view section-gap-md">
          <div className="container">
            <div className="row">
              <div className="col-lg-12 mb-4">
                <div className="title-sec wow fadeInUp" data-wow-delay="0.3s">
                  <h2 className="title-25">Events Near Me</h2>
                </div>
              </div>

              <div className="col-lg-12">
                <div className="text-center py-5">
                  <h2>No nearby event nearby you</h2>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Events Near Me End*/}

      {/* Events List */}
      <section className="event-sec section-gap-lg">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 mb-4">
              <div className="title-sec wow fadeInUp" data-wow-delay="0.3s">
                <h2 className="title-25">Upcoming Event In Town</h2>
              </div>
            </div>
          </div>
          <div className="club-result-row section-gap-btm wow fadeInUp">
            {/* //event-sec-list */}
            {showPageLoader ? (
              <LoadingSpinner className="spinner-container--page" />
            ) : (
              <InfiniteScroll
                dataLength={visibleEvents?.length}
                next={hasNextPage ? fetchNextPage : refetch}
                hasMore={hasNextPage}
                loader={<LoadingSpinner className="spinner-container--inline" />}
                refreshFunction={refetch}
              >
                <div className="card-list">
                  <div className="row">
                    {visibleEvents?.length ? (
                      visibleEvents.map((event, index) => (
                        <EventCard className="col-lg-6 col-xl-3 col-md-6 col-12 wow fadeInUp" event={event} key={index} index={index} isCard={true} />
                      ))
                    ) : (
                      <></>
                    )}
                  </div>
                  {!visibleEvents?.length && (
                    <NoContentAvailable
                      title="No content available"
                      message="No content available on Event page."
                    />
                  )}
                </div>
              </InfiniteScroll>
            )}
          </div>
        </div>
      </section>

      {/* Events List End */}

      {/* filter Popup */}
      {/* Modal */}
      <FilterComp
        isModalOpen={filterComponent}
        onPressApply={onPressApply}
        onPressCancel={onPressCancel}
        isArtistFilter={false}
        isEventFilter={true}
        selectedFilter={filteredData}
      />
      {/* filter Popup End */}

      <a href="#" className="back-to-top">
        <i className="fa fa-chevron-up"></i>
      </a>
    </Layout>
  );
};

export default Events;
